import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Eye, MapPin, Loader2, ImagePlus, X, Trash2 } from "lucide-react";
import { api, errText, photoUrl } from "../lib/api";
import { CITY_COORDS, PROV_CENTER } from "../lib/constants";
import { compressImage } from "../lib/image";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { LostFoundId } from "./LostFoundId";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";

const dot = (color, size, pulse) => L.divIcon({
  className: "",
  html: `<div style="position:relative;width:${size}px;height:${size}px">
    ${pulse ? `<span style="position:absolute;inset:-6px;border-radius:9999px;background:${color};opacity:.35;animation:ping 1.4s cubic-bezier(0,0,.2,1) infinite"></span>` : ""}
    <span style="position:absolute;inset:0;border-radius:9999px;background:${color};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></span></div>`,
  iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -size / 2],
});
const ICON_HOME = L.divIcon({
  className: "",
  html: `<svg width="30" height="40" viewBox="0 0 30 40"><path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.7 23.3 0 15 0z" fill="#12234A"/><circle cx="15" cy="15" r="6" fill="#F5551E"/></svg>`,
  iconSize: [30, 40], iconAnchor: [15, 40], popupAnchor: [0, -38],
});
const ICON_NEW = dot("#F5551E", 20, true);
const numIcon = (n, latest) => {
  const size = latest ? 28 : 22;
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;width:${size}px;height:${size}px">
      ${latest ? `<span style="position:absolute;inset:-6px;border-radius:9999px;background:#DC2626;opacity:.35;animation:ping 1.4s cubic-bezier(0,0,.2,1) infinite"></span>` : ""}
      <span data-trail-num="${n}" style="position:absolute;inset:0;border-radius:9999px;background:${latest ? "#DC2626" : "#F87171"};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);color:#fff;font:700 ${latest ? 12 : 10}px/1 sans-serif;display:flex;align-items:center;justify-content:center">${n}</span></div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -size / 2],
  });
};
// Arrow at the midpoint of the newest leg, rotated toward the latest sighting.
const arrowIcon = (deg) => L.divIcon({
  className: "",
  html: `<div data-testid="trail-arrow" style="width:26px;height:26px;transform:rotate(${deg}deg);display:flex;align-items:center;justify-content:center">
    <svg width="26" height="26" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#fff" stroke="#DC2626" stroke-width="2"/><path d="M7 12h9M12 7l5 5-5 5" stroke="#DC2626" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg></div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});
const legArrow = (a, b) => {
  const dx = (b[1] - a[1]) * Math.cos(((a[0] + b[0]) / 2) * Math.PI / 180);
  const dy = b[0] - a[0];
  return { pos: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], deg: (-Math.atan2(dy, dx) * 180) / Math.PI };
};

const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const fmt = (iso, lang) => new Date(iso).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "medium", timeStyle: "short" });

function FitTrail({ points }) {
  const map = useMap();
  const key = points.map((p) => p.join(",")).join("|");
  useEffect(() => {
    if (points.length > 1) map.fitBounds(L.latLngBounds(points).pad(0.25), { maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

function ClickToPin({ active, onPick }) {
  useMapEvents({ click: (e) => { if (active) onPick([e.latlng.lat, e.latlng.lng]); } });
  return null;
}

export function SightingsMap({ listing, canManage }) {
  const { user, setAuthOpen } = useAuth();
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [picking, setPicking] = useState(false);
  const [pin, setPin] = useState(null);
  const [form, setForm] = useState({ seen_at: nowLocal(), note: "", photo: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const center = useMemo(() => CITY_COORDS[(listing.city || "").trim().toLowerCase()] || PROV_CENTER[listing.province] || PROV_CENTER.all, [listing.city, listing.province]);
  const canAdd = listing.lf_type === "lost" && listing.status === "active" && !listing.reunited_at;
  const trail = useMemo(() => [center, ...[...items].reverse().map((s) => [s.lat, s.lng])], [center, items]);
  const arrow = trail.length >= 2 ? legArrow(trail[trail.length - 2], trail[trail.length - 1]) : null;

  useEffect(() => { api.get(`/listings/${listing.id}/sightings`).then((r) => setItems(r.data)).catch(() => {}); }, [listing.id]);

  const start = () => { if (!user) return setAuthOpen(true); setPicking(true); setPin(null); setForm({ seen_at: nowLocal(), note: "", photo: "" }); };

  const onPhoto = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo: data.path }));
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!pin) return toast.error(t("sg_need_pin"));
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/sightings`, { lat: pin[0], lng: pin[1], seen_at: new Date(form.seen_at).toISOString(), note: form.note, photo: form.photo });
      setItems((s) => [data, ...s].sort((a, b) => b.seen_at.localeCompare(a.seen_at)));
      setPicking(false); setPin(null);
      toast.success(t("sg_sent"));
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  const remove = async (sid) => {
    try { await api.delete(`/listings/${listing.id}/sightings/${sid}`); setItems((s) => s.filter((x) => x.id !== sid)); }
    catch (e) { toast.error(errText(e)); }
  };

  if (!canAdd && !items.length) return null;

  return (
    <section id="sightings" className="bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="sightings-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 flex-wrap"><Eye size={18} className="text-red-600" /> {t("sg_title")} <span className="text-slate-400 font-normal text-sm" data-testid="sightings-count">({items.length})</span> <LostFoundId listing={listing} testid="sightings-lf-id" /></h3>
          {canAdd && <p className="text-sm text-slate-500 mt-0.5">{t("sg_sub")}</p>}
        </div>
        {canAdd && !picking && (
          <Button onClick={start} data-testid="sighting-start-btn" className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold gap-2"><MapPin size={16} /> {t("sg_report")}</Button>
        )}
      </div>

      {picking && <p className="mt-3 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="sighting-pick-hint">{t("sg_pick")}</p>}

      <div className={`relative z-0 mt-4 rounded-xl overflow-hidden border h-[340px] ${picking ? "border-red-400 ring-2 ring-red-200 cursor-crosshair" : "border-slate-200"}`} data-testid="sightings-map">
        <MapContainer center={center} zoom={12} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickToPin active={picking} onPick={setPin} />
          <FitTrail points={trail} />
          <Marker position={center} icon={ICON_HOME}><Popup>{listing.city}, {listing.province}</Popup></Marker>
          {trail.length >= 2 && <Polyline positions={trail} pathOptions={{ color: "#DC2626", weight: 3, dashArray: "2 8", lineCap: "round", opacity: 0.85 }} />}
          {arrow && <Marker position={arrow.pos} icon={arrowIcon(arrow.deg)} interactive={false} zIndexOffset={500} />}
          {items.map((s, i) => (
            <Marker key={s.id} position={[s.lat, s.lng]} icon={numIcon(items.length - i, i === 0)} zIndexOffset={i === 0 ? 1000 : 0}>
              <Popup>
                <div className="w-48" data-testid={`sighting-popup-${s.id}`}>
                  {i === 0 && <span className="text-[10px] font-bold text-white bg-red-600 rounded px-1.5 py-0.5">{t("sg_latest").toUpperCase()}</span>}
                  <p className="font-semibold text-[#12234A] text-sm mt-1">{fmt(s.seen_at, lang)}</p>
                  <p className="text-xs text-slate-500">{t("sg_by")} {s.user_name}</p>
                  {s.note && <p className="text-xs text-slate-600 mt-1">{s.note}</p>}
                  {s.photo && <img src={photoUrl(s.photo)} alt="" className="mt-1.5 h-24 w-full rounded-lg object-cover" />}
                </div>
              </Popup>
            </Marker>
          ))}
          {pin && <Marker position={pin} icon={ICON_NEW} />}
        </MapContainer>
      </div>

      {picking && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="sighting-form">
          <div>
            <Label>{t("sg_when")}</Label>
            <Input type="datetime-local" value={form.seen_at} max={nowLocal()} onChange={(e) => setForm((f) => ({ ...f, seen_at: e.target.value }))} data-testid="sighting-when" className="mt-1 h-10 rounded-xl" />
          </div>
          <div>
            <Label>{t("sg_photo")}</Label>
            <div className="mt-1 flex items-center gap-2 h-10">
              {form.photo ? (
                <span className="relative h-10 w-14 rounded-lg overflow-hidden border border-slate-200">
                  <img src={photoUrl(form.photo)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, photo: "" }))} data-testid="sighting-photo-remove" className="absolute top-0 right-0 h-4 w-4 bg-black/60 text-white flex items-center justify-center"><X size={10} /></button>
                </span>
              ) : (
                <label data-testid="sighting-photo-upload" className="flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] cursor-pointer hover:underline">
                  {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} {t("sg_photo")}
                  <input type="file" accept="image/*" onChange={onPhoto} className="hidden" />
                </label>
              )}
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>{t("sg_note")}</Label>
            <Textarea value={form.note} maxLength={500} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="sighting-note" rows={2} placeholder={t("sg_note_ph")} className="mt-1 rounded-xl" />
          </div>
          <div className="sm:col-span-2 flex gap-2">
            <Button onClick={submit} disabled={busy || uploading} data-testid="sighting-submit-btn" className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold">
              {busy ? <Loader2 size={16} className="animate-spin" /> : t("sg_submit")}
            </Button>
            <Button variant="outline" onClick={() => { setPicking(false); setPin(null); }} data-testid="sighting-cancel-btn" className="rounded-xl">{t("sg_cancel")}</Button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2" data-testid="sightings-list">
        {!items.length && <p className="text-sm text-slate-400">{t("sg_none")}</p>}
        {items.map((s, i) => (
          <div key={s.id} className={`flex gap-3 items-start rounded-xl border p-3 ${i === 0 ? "border-red-200 bg-red-50/60" : "border-slate-100"}`} data-testid={`sighting-item-${s.id}`}>
            <span data-testid={`sighting-num-${s.id}`} className={`h-6 w-6 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold text-white ${i === 0 ? "bg-red-600" : "bg-red-300"}`}>{items.length - i}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#12234A]">{fmt(s.seen_at, lang)} {i === 0 && <span className="ml-1 text-[10px] font-bold text-white bg-red-600 rounded px-1.5 py-0.5 align-middle">{t("sg_latest").toUpperCase()}</span>}</p>
              <p className="text-xs text-slate-500">{t("sg_by")} {s.user_name}</p>
              {s.note && <p className="text-sm text-slate-600 mt-1">{s.note}</p>}
            </div>
            {s.photo && <img src={photoUrl(s.photo)} alt="" className="h-14 w-16 rounded-lg object-cover shrink-0" />}
            {canManage && (
              <button onClick={() => remove(s.id)} data-testid={`sighting-remove-${s.id}`} title={t("sg_remove")} className="h-8 w-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center shrink-0"><Trash2 size={15} /></button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
